/**
 * Resolve a set of bundle (product) ids to their public `StorefrontBundle`
 * data for the cart (TNK-212 phase 2b) — the bundle analogue of
 * useStorefrontCardLookup. Fetches the listed-bundles feed once and maps by id;
 * any cart id not in the feed (sold, un-listed, or reserved by someone else
 * since it was added) lands in `missing` so the cart can flag + bulk-remove it,
 * matching the card flow.
 */
import { useEffect, useState } from "react";

import { fetchStorefrontBundles, type StorefrontBundle } from "./storefront";

export interface BundleLookup {
  bundles: StorefrontBundle[];
  missing: Set<string>;
  phase: "loading" | "ready" | "error";
}

export function useStorefrontBundleLookup(productIds: string[]): BundleLookup {
  const key = productIds.join(",");
  const [all, setAll] = useState<StorefrontBundle[]>([]);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("ready");

  useEffect(() => {
    if (productIds.length === 0) {
      setAll([]);
      setPhase("ready");
      return;
    }
    let cancelled = false;
    setPhase("loading");
    void fetchStorefrontBundles()
      .then((b) => {
        if (cancelled) return;
        setAll(b);
        setPhase("ready");
      })
      .catch(() => {
        if (!cancelled) setPhase("error");
      });
    return () => {
      cancelled = true;
    };
    // `key` captures the id set; productIds is a fresh array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const byId = new Map(all.map((b) => [b.id, b]));
  const bundles = productIds
    .map((id) => byId.get(id))
    .filter((b): b is StorefrontBundle => !!b);
  const found = new Set(bundles.map((b) => b.id));
  const missing = new Set(productIds.filter((id) => !found.has(id)));

  return { bundles, missing, phase };
}
